import { inject, Injectable } from '@angular/core';
import { defer, Observable, of } from 'rxjs';
import { ProductTourStatus } from '../enums/product-tour-status';
import {
    ProductTourRecord,
    ProductTourStateStore,
} from '../models/product-tour';
import { localStorageToken } from '../tokens/localStorage.token';
import { CurrentUserService } from './current-user.service';

const STORAGE_KEY_PREFIX = 'lemunzo-product-tour';

/**
 * Per-user product tour state kept in the browser's localStorage (per device).
 * Swap for a backend-backed implementation by changing the factory of
 * `PRODUCT_TOUR_STATE_STORE`.
 */
@Injectable({ providedIn: 'root' })
export class LocalStorageProductTourStateStore implements ProductTourStateStore {
    private readonly currentUserService = inject(CurrentUserService);
    private readonly storage: Storage = inject(localStorageToken);

    get(tourId: string): Observable<ProductTourRecord | undefined> {
        // defer() turns a missing user or blocked storage (thrown errors) into an error notification
        return defer(() => {
            const value = this.storage.getItem(this.getStorageKey(tourId));
            return of(value ? this.parseRecord(value) : undefined);
        });
    }

    set(tourId: string, record: ProductTourRecord): Observable<void> {
        return defer(() => {
            this.storage.setItem(
                this.getStorageKey(tourId),
                JSON.stringify(record)
            );
            return of(undefined);
        });
    }

    private getStorageKey(tourId: string): string {
        const userId = this.currentUserService.getCurrentUser()?.id;

        if (!userId) {
            throw new Error('Product tour state requires a signed-in user');
        }

        return `${STORAGE_KEY_PREFIX}:${userId}:${tourId}`;
    }

    /** Corrupted or unknown values are treated as "no record" and get overwritten. */
    private parseRecord(value: string): ProductTourRecord | undefined {
        try {
            const record = JSON.parse(value) as Partial<ProductTourRecord>;
            const validStatus = Object.values(ProductTourStatus).includes(
                record?.status as ProductTourStatus
            );

            return typeof record?.version === 'number' && validStatus
                ? (record as ProductTourRecord)
                : undefined;
        } catch {
            return undefined;
        }
    }
}
